import '../../../../scss/pages/admin/_admin-clients.scss';

import {
  DialogActionButton,
  Radio,
  RadioListItem,
  saveCallbackStatus,
  TextField,
  Toggle,
} from '@finpay-development/shared-components';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Divider, IconButton, MenuItem, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { formatNumberToUSD } from '../../../../shared/calculators';
import { notNegativeOneValidator } from '../../../../shared/misc/regex';
import { RootState } from '../../../../shared/state/root-reducer';
import { AppDispatch } from '../../../../shared/state/store';
import { Utils } from '../../../../shared/utils';
import { commonFields } from '../../../../shared/validation/schemas';
import StripeCardField from '../../../../stripe/components/StripeCardField';
import { setPatientTakePaymentToken, setRecurringPatientTakePaymentToken } from '../../../state/patient-slice';
import { addPatientPaymentMethod, getPaymentMethods, makePayment } from '../../../state/patient-thunk';
import { PatientPaymentProgram } from '../../models/patient-payment-program';
import { Payment } from '../../models/payment';
import { PaymentDetail } from '../../models/payment-detail';
import { PAYMENT_METHOD_TYPES, PaymentMethod } from '../../models/payment-method';
import { PAYMENT_TYPES } from '../../models/payment-type';
import { StripePaymentMethod } from '../../models/stripe-payment-method';

interface TakePaymentModalProps {
  open: boolean;
  handleModalClose: () => void;
}

export function TakePaymentModal(props: TakePaymentModalProps) {
  const { open, handleModalClose } = props;

  const [enableSaveButton, setEnableSaveButton] = useState(false);
  const [isStripeEmpty, setIsStripeEmpty] = useState(true);
  const [useExistingSource, setUseExistingSource] = useState(true);
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);
  const [typeOfTakePayment, setTypeOfTakePayment] = useState('');

  const formRef: any = useRef();
  const dispatch = useDispatch<AppDispatch>();

  const stateFields = {
    stripeToken: useSelector(
      (state: RootState) => state.patientContext?.patientTakePaymentTokens?.token
    ),
    stripeRecurringCardToken: useSelector(
      (state: RootState) => state.patientContext?.recurringPatientTakePaymentTokens?.token
    ),
    selectedPatient: useSelector(
      (state: RootState) => state.patientContext?.selectedPatient
    ),
    selectedEncounter: useSelector(
      (state: RootState) => state.patientContext?.selectedEncounter
    ),
    patientPaymentProgram: useSelector((state: RootState) => {
      return state?.patientContext?.selectedEncounter?.patientPaymentProgram?.length > 0 ?
      state.patientContext.selectedEncounter.patientPaymentProgram[state.patientContext.selectedEncounter.patientPaymentProgram.length - 1] : {} as PatientPaymentProgram
    }),
    paymentMethods: useSelector((state: RootState) => {
      return state.patientContext.selectedEncounter?.patientPaymentMethods?.length > 0 &&
      state.patientContext.selectedEncounter.patientPaymentMethods
        .filter((paymentMethod: StripePaymentMethod) => paymentMethod?.object === 'card' && (
        (paymentMethod?.metadata.metaData_paymentMethodType?.includes("RECURRING")) || (paymentMethod?.metadata?.isRecurring)))
    }) as StripePaymentMethod[],
    transactions: useSelector((state: RootState) => {
      return state.patientContext.selectedEncounter?.patientTransactions?.length > 0 && state.patientContext.selectedEncounter.patientTransactions
    }),
    paymentMethodsFailed: useSelector((state: RootState) => {
      return state.patientContext?.isError?.paymentMethods
    }),
    missingAddressError: useSelector((state: RootState) => state.patientContext.isError.missingAddress),
  }
  const {
    stripeToken, stripeRecurringCardToken, selectedPatient, selectedEncounter, patientPaymentProgram, paymentMethods, paymentMethodsFailed, missingAddressError
  } = stateFields;

  const pfrBalance = patientPaymentProgram?.patientPaymentSchedule?.pfrBalance;

  const isWorkFlowStatusGoodStandingOrConvert = (
    (selectedEncounter?.workflow?.workflowStatus.workflowStatusId === 8) || (selectedEncounter?.workflow?.workflowStatus.workflowStatusId === 12)
  ) // 'good standing' and 'converted' ids
  const nextOrMissedPaymentValue = patientPaymentProgram?.patientPaymentSchedule?.missedPmts > 0 && !isWorkFlowStatusGoodStandingOrConvert ? (
    patientPaymentProgram?.patientPaymentSchedule?.missedPmts * patientPaymentProgram?.patientPaymentSchedule?.paymentDueAmt
  ) : (
    patientPaymentProgram?.patientPaymentSchedule?.paymentDueAmt
  )

  const isNextPmtMoreThanBalance = (nextOrMissedPaymentValue > pfrBalance) // if the 'next payment' in the payment plan is more than the remaining balance then we will hide the next payment button.

  const getPaymentAmount = () => {
    const currentVals = formRef?.current?.values
    if (currentVals?.takePaymentType === 'missed') {
      return nextOrMissedPaymentValue
    } else if (currentVals?.takePaymentType === 'balance') {
      return patientPaymentProgram?.patientPaymentSchedule?.pfrBalance
    } else {
      return parseFloat(currentVals?.amount)
    }
  }

  const patientsAndChampionsList = Utils.getPatientsAndChampionsList(selectedPatient, selectedEncounter, true); // payor can be anyone in this list

  const getPaymentMethod = () => {
    const currentFormValues = formRef?.current?.values;
    let response = {};
    if (useExistingSource) {
      response = {
        externalPaymentId: JSON.parse(currentFormValues?.paymentSources)?.id
      }
    } else {
      response = {externalPaymentId: stripeToken}
    }
    return response;
  }

  const getPayorId = () => {
    let payorId = undefined;
    const currentVals = formRef?.current?.values;

    if (useExistingSource) {
      payorId = (paymentMethods?.length > 0 && formRef?.current?.values?.paymentSources) ?
        (parseInt(JSON.parse(currentVals?.paymentSources)?.metadata?.metaData_payorId) || undefined) : undefined;
    } else if (!useExistingSource && savePaymentMethod) {
      payorId = ((currentVals?.payorName === selectedPatient?.patientId) || currentVals?.payorName === "-1") ? undefined : parseInt(currentVals?.payorName)
    }
    return payorId;
  }

  const mapToPayment = () => {
    const currentDateTime = new Date();

    return {
      paymentId: 0, // always 0 when we are POSTing a new payment. use paymentID for release payment
      isManual: true, // hardcoded
      isAuthExpired: false, // hardcoded
      paymentGroupId: `${patientPaymentProgram?.patientPaymentSchedule?.patientPaymentScheduleId}`,
      patientPaymentScheduleId: patientPaymentProgram?.patientPaymentSchedule?.patientPaymentScheduleId,
      patientId: selectedEncounter.patientId,
      patientEncounterId: selectedEncounter.patientEncounterId,
      paymentMethods: [{
        ...PAYMENT_METHOD_TYPES.CARD, // sources are always card.
        ...getPaymentMethod(),
        payorId: getPayorId(),
      }],
      paymentAmt: getPaymentAmount(),
      paymentType: PAYMENT_TYPES.SPECIALIST, // always specialist for 'Take Payment'
      paymentInitDt: currentDateTime.toISOString(),
      isAuthOnly: false, // preauthorizes and then charges card
    } as Payment;
  }

  const mapToRecurringPayment = () => {
    const currentVals = formRef?.current?.values;

    const mappedPaymentMethods = [{
        paymentMethodId: 0,
        payorId: getPayorId(),
        paymentAmt: 0, // adding 0 as payment amount to stop this from being charged in Billing engine.
        scheduleStartDt: "", // no date - this payment method will not be used in billing engine
        externalPaymentMethodId: PAYMENT_METHOD_TYPES.CARD.externalPaymentMethodId,
        paymentMethodTypeId: PAYMENT_METHOD_TYPES.CARD.paymentMethodTypeId,
        externalPaymentId: stripeRecurringCardToken,
        receiptEmail: selectedPatient?.contact?.email,
        nameOnCard: currentVals?.nameOnCard,
      }] as PaymentMethod[]
    const paymentType = PAYMENT_TYPES.RECURRING

    return {
      paymentMethods: mappedPaymentMethods,
      paymentType: paymentType
    } as PaymentDetail
  }

  async function handleSave() {
    const paymentInfo = mapToPayment();
    await dispatch(makePayment({payment: paymentInfo}))
    if (savePaymentMethod && !useExistingSource) {
      const recurringPaymentInfo = mapToRecurringPayment();
      await dispatch(addPatientPaymentMethod(
        {
          patientId: selectedEncounter?.patientId,
          encounterId: selectedEncounter?.patientEncounterId,
          paymentDetail: recurringPaymentInfo,
          isAccountHolder: false
        }
      ));
    }
    handleModalClose();
  }

  useEffect(() => {
    if (!open && (stripeToken || stripeRecurringCardToken)) { // cleanup token state
      dispatch(setPatientTakePaymentToken({}))
      dispatch(setRecurringPatientTakePaymentToken({}))
    }
  }, [dispatch, open, useExistingSource, stripeToken, stripeRecurringCardToken]);

  useEffect(() => {
      checkIfIsValid(formRef?.current?.values)
    // eslint-disable-next-line
  }, [stripeToken, useExistingSource, typeOfTakePayment, formRef?.current?.values?.takePaymentType, formRef?.current?.values]);

  useEffect(() => {
    if ((selectedEncounter?.patientId !== 0) && (selectedEncounter?.patientEncounterId !== 0) && open && !missingAddressError) {
      dispatch(getPaymentMethods({
        patientId: selectedEncounter?.patientId,
        encounterId: selectedEncounter?.patientEncounterId
      }));
    }
    // eslint-disable-next-line
  }, [open]);

  const reFetchPaymentMethods = () => {
    if (!missingAddressError) {
      dispatch(getPaymentMethods({
        patientId: selectedEncounter?.patientId,
        encounterId: selectedEncounter?.patientEncounterId
      }));
    }
  }

  function handleSaveCallback(saveSuccessful: boolean) {
    handleModalClose()
  }

  const stripeTokenCallback = (data: { token?: string, cardToken?: string, bankToken?: string }) => {
    const { token, cardToken, bankToken } = data
    if (token && cardToken) {
      dispatch(setPatientTakePaymentToken({ token: token, cardToken: cardToken }))
    } else if (bankToken) {
      dispatch(setPatientTakePaymentToken({ bankToken: bankToken }))
    }
  }

  const stripeRecurringPaymentTokenCallback = (data: { token?: string, cardToken?: string }) => {
    const { token, cardToken } = data
    if (token && cardToken) {
      dispatch(setRecurringPatientTakePaymentToken({ token: token, cardToken: cardToken }))
    }
  }

  const stripeDisabledCallback = (isDisabledFromStripe: boolean ) => {
    setIsStripeEmpty(isDisabledFromStripe)
  };

  const initialValues = {
    payorName: "-1",
    takePaymentType: "balance", // 'missed', 'balance', 'other'
    amount: "",
    useExistingSource: paymentMethods.length > 0 ? true : false,
    paymentSources: "-1",
    nameOnCard: "",
    savePaymentMethod: false,
  };

  const commonSchema = {
    payorName: savePaymentMethod && !useExistingSource ? commonFields.REQUIRED_DROPDOWN : Yup.string(),
    takePaymentType: Yup.string().min(1),
    amount: Yup.number().max(pfrBalance, `You cannot take a payment greater than the remaining balance of ${formatNumberToUSD(pfrBalance)}`),
    useExistingSource: Yup.boolean(),
    nameOnCard: useExistingSource ? Yup.string() : Yup.string().min(1), // required when useExistingSource === false.
    paymentSources: useExistingSource ? Yup.string().min(1).matches(notNegativeOneValidator, 'Must select a value') : Yup.string(), // required when useExistingSource === true.
    savePaymentMethod: Yup.boolean(),
  }

  const otherSchema = {
    ...commonSchema,
    amount: Yup.number()
      .typeError('Amount must be a number')
      .min(.50, 'Minimum payment is 50 cents')
      .max(pfrBalance, `You cannot take a payment greater than the remaining balance of ${formatNumberToUSD(pfrBalance)}`),
  }

  const balanceOrNextPaymentSchema = {
    ...commonSchema,
    amount: Yup.string(),
  }

  const getValidationSchema = () => {
    return typeOfTakePayment === 'other'
      ? otherSchema : balanceOrNextPaymentSchema
  }

  let validationSchema = Yup.object(getValidationSchema());

  function checkIfIsValid(
    value: {
      takePaymentType: string,
      amount: string,
      useExistingSource: boolean,
      paymentSources: string,
      nameOnCard: string,
      savePaymentMethod: boolean
    }
  ) {
    if ((value?.useExistingSource !== useExistingSource) && stripeToken) {
      dispatch(setPatientTakePaymentToken({})) // need to reset stripe token each time we change 'use existing source'
      dispatch(setRecurringPatientTakePaymentToken({}))
    }
    setUseExistingSource(value?.useExistingSource);
    setSavePaymentMethod(value?.savePaymentMethod);
    validationSchema
      .validate(value)
      .then(() => {
        setEnableSaveButton(true);
        if (
            ((value?.useExistingSource) && (+value?.paymentSources === -1)) ||
            ((value?.takePaymentType === 'other') && (value?.amount.toString().trim() === ""))
          ) {
            setEnableSaveButton(false);
          }
      })
      .catch((err) => {
        setEnableSaveButton(false);
      });
  }

  return (
    <Dialog
      scroll="body"
      className="modal client-modal"
      open={open}
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle>
        <span className="title">Take a Credit Card Payment</span>
      </DialogTitle>
      <DialogContent>
        <Formik
          enableReinitialize
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          validate={checkIfIsValid}
          onSubmit={() => { }}
        >
          {(formik) => (
            <Form>
              <Typography variant="subtitle2">
                Payment Amount
              </Typography>
              <Grid
                container
                className="item"
                direction="row"
                justifyContent="space-around"
                alignItems="center"
              >
                {!isNextPmtMoreThanBalance && (
                  patientPaymentProgram?.patientPaymentSchedule?.missedPmts > 0 && !isWorkFlowStatusGoodStandingOrConvert ? (
                    <>
                      <Grid item xs={6} className="pt-1 pl-1">
                        <Box justifyContent="flex-start">
                          <Radio
                            id="missedPayment"
                            name="takePaymentType"
                            label="Missed Payment"
                            value="missed"
                            checked={"missed" === formik.values['takePaymentType']}
                            onChange={
                              (e: Event) => {
                                setTypeOfTakePayment('missed')
                                formik.handleChange(e)
                              }
                            }
                            />
                        </Box>
                        <Box justifyContent="flex-start" className="ml-2 pl-8" style={{marginTop: -12}}>
                          <Typography variant="caption">
                            {(patientPaymentProgram?.patientPaymentSchedule?.lastPaymentFailureDt || patientPaymentProgram?.patientPaymentSchedule?.missedPmts)
                              ? `${patientPaymentProgram?.patientPaymentSchedule?.lastPaymentFailureDt} (${formatNumberToUSD(patientPaymentProgram?.patientPaymentSchedule?.missedPmts * patientPaymentProgram?.patientPaymentSchedule?.paymentDueAmt)})`
                              : ''}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box display="flex" justifyContent="flex-end">
                          <Typography variant="subtitle2">
                            {patientPaymentProgram?.patientPaymentSchedule?.missedPmts
                              ? formatNumberToUSD(patientPaymentProgram?.patientPaymentSchedule?.missedPmts * patientPaymentProgram?.patientPaymentSchedule?.paymentDueAmt)
                              : 'Not found'}
                          </Typography>
                        </Box>
                      </Grid>
                    </>
                  ) : patientPaymentProgram?.patientPaymentSchedule?.paymentFreq !== 'F' && (
                    <>
                      <Grid item xs={6} className="pt-1 pl-1">
                        <Box justifyContent="flex-start">
                          <Radio
                            id="missedPayment"
                            name="takePaymentType"
                            label="Next Payment"
                            value="missed"
                            checked={"missed" === formik.values['takePaymentType']}
                            onChange={
                              (e: Event) => {
                                setTypeOfTakePayment('missed')
                                formik.handleChange(e)
                              }
                            }
                          />
                        </Box>
                        <Box justifyContent="flex-start" className="ml-2 pl-8" style={{marginTop: -12}}>
                          <Typography variant="caption">
                            {patientPaymentProgram?.patientPaymentSchedule?.paymentDueAmt ? formatNumberToUSD(patientPaymentProgram?.patientPaymentSchedule?.paymentDueAmt) : ''}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box display="flex" justifyContent="flex-end">
                          <Typography variant="subtitle2">
                            {patientPaymentProgram?.patientPaymentSchedule?.paymentDueAmt ? formatNumberToUSD(patientPaymentProgram?.patientPaymentSchedule?.paymentDueAmt) : ''}
                          </Typography>
                        </Box>
                      </Grid>
                    </>
                  )
                )}
                <Grid item xs={12}>
                  <RadioListItem
                    id="balance"
                    name="takePaymentType"
                    radioLabel="Balance"
                    value="balance"
                    checked={"balance" === formik.values['takePaymentType']}
                    onChange={
                      (e: Event) => {
                        setTypeOfTakePayment('balance')
                        formik.handleChange(e)
                      }
                    }
                    endOfRowLabel={`${formatNumberToUSD(patientPaymentProgram?.patientPaymentSchedule?.pfrBalance)}`}
                  />
                </Grid>
                <Grid item xs={9} className="pt-1 pl-1">
                  <Box justifyContent="flex-start">
                    <Radio
                      id="other"
                      name="takePaymentType"
                      label="Other"
                      value="other"
                      checked={"other" === formik.values['takePaymentType']}
                      onChange={
                        (e: Event) => {
                          setTypeOfTakePayment('other')
                          formik.handleChange(e)
                        }
                      }
                    />
                  </Box>
                </Grid>
                <Grid item xs={3} >
                  <Box display="flex" justifyContent="flex-end">
                    <TextField
                      error={
                        formik.touched["amount"] && formik.errors["amount"]
                      }
                      startAdornment={<AttachMoneyIcon />}
                      name="amount"
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={formik.values.takePaymentType !== 'other'}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} className="py-6">
                  <Divider />
                </Grid>
                <Grid container>
                  <Grid xs={12} item>
                    <Typography variant="h4">
                      Use Existing Source
                    </Typography>
                  </Grid>
                  <Grid item xs={12} className="pb-6">
                    <Toggle
                      name="useExistingSource"
                      value={formik.values.useExistingSource}
                      formik={formik}
                    />
                  </Grid>
                  {!formik?.values?.useExistingSource && (
                    <>
                      <Grid xs={12} item>
                        <Typography variant="h4">
                          Save as Recurring Payment
                        </Typography>
                      </Grid>
                      <Grid item xs={12} className="pb-6">
                        <Toggle
                          name="savePaymentMethod"
                          value={formik.values.savePaymentMethod}
                          formik={formik}
                        />
                      </Grid>
                    </>
                  )}
                  {!formik?.values?.useExistingSource && formik?.values?.savePaymentMethod && (
                    <>
                      <Grid item xs={12} className="pb-2">
                        <Typography variant="h4">
                          Payor
                        </Typography>
                      </Grid>
                      <Grid item xs={6} className="pb-6">
                        <TextField
                          select={true}
                          error={
                            formik.touched["payorName"] && formik.errors["payorName"]
                          }
                          label="Payor Name"
                          name="payorName"
                          value={formik.values.payorName}
                          placeholder="Select"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          <MenuItem value="-1">Select</MenuItem>
                          {patientsAndChampionsList.map((payor: { name: string, id: number }) => (
                            <MenuItem key={payor.id} value={payor.id}>
                              {payor.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </>
                  )}
                </Grid>
                {formik.values.useExistingSource ? (
                  <>
                    <Grid xs={6} item>
                      <TextField
                        select={true}
                        error={
                          formik.touched["paymentSources"] && formik.errors["paymentSources"]
                        }
                        label="Payment Sources"
                        name="paymentSources"
                        value={formik.values.paymentSources}
                        className="pb-4"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      >
                        <MenuItem value="-1">
                          Select Payment Source
                        </MenuItem>
                        {paymentMethods && paymentMethods?.length > 0 && paymentMethods.map((source: StripePaymentMethod, index: number) => (
                          <MenuItem
                            key={(source?.id) ? source.id : index }
                            value={JSON.stringify(source)}
                          >
                            {Utils.convertPaymentMethodToReadableInfo(source)}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    {paymentMethodsFailed && (
                      <>
                        <Typography variant="h4" className="pl-6">
                          Refresh payment methods
                        </Typography>
                        <Box display="flex" justifyContent="flex-end" marginTop="-4rem">
                          <IconButton onClick={() => reFetchPaymentMethods()} size="large">
                            <RefreshIcon fontSize='large' className="icon" style={{color: '297ec1'}} />
                          </IconButton>
                        </ Box>
                      </>
                    )}
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={6}>
                    <TextField
                      error={
                        formik.touched["nameOnCard"] && formik.errors["nameOnCard"]
                      }
                      label="Name on Card"
                      placeholder="Enter Name on Card"
                      name="nameOnCard"
                      value={formik.values.nameOnCard}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Grid>
                  <Grid item xs={6} />
                  <Grid item xs={12} className="my-2">
                    <StripeCardField
                      disabledCallback={stripeDisabledCallback}
                      tokenCallback={stripeTokenCallback}
                      data={
                        {
                          name: formik.values.nameOnCard,
                          address_country: 'US'
                        }
                      }
                      recurringPayment={!formik.values.useExistingSource && formik.values.savePaymentMethod}
                      recurringPaymentTokenCallback={stripeRecurringPaymentTokenCallback}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions>
        <DialogActionButton
          isEnabled={useExistingSource ? enableSaveButton : enableSaveButton && !isStripeEmpty && (!!stripeToken)}
          savebuttonText='Pay'
          saveStatus={saveCallbackStatus.none}
          executeSave={handleSave}
          handleCallbackSave={handleSaveCallback}
          handleCallbackCancel={handleModalClose}
        />
      </DialogActions>
    </Dialog>
  );
}
